import { useMemo } from "react";

import { formatUserName } from "@impero-com/common/utils/formatting";
import { useTranslation } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import { TST_COLUMN_SIZE, TSTTable } from "@impero-com/ui";
import {
    createColumnHelper,
    getCoreRowModel,
    useReactTable,
} from "@tanstack/react-table";

import type {
    ConflictAssignee,
    SegregationOfDutiesConflict,
} from "api/generated/model";
import { WebRoute } from "routes";

interface SegregationOfDutiesConflictsTableProps {
    conflicts: SegregationOfDutiesConflict[];
}

export function SegregationOfDutiesConflictsTable({
    conflicts,
}: SegregationOfDutiesConflictsTableProps) {
    const { t } = useTranslation();

    const columns = useMemo(() => {
        const columnHelper = createColumnHelper<SegregationOfDutiesConflict>();
        return [
            columnHelper.accessor((conflict) => conflict.control.title, {
                id: "control",
                header: () =>
                    t("ControlPolicies_SegregationOfDuties_Conflicts_Control"),
                cell: (info) => (
                    <a
                        href={`${WebRoute.CONTROLS}/${info.row.original.control.id}`}
                        target="_blank"
                    >
                        {info.getValue()}
                    </a>
                ),
                size: TST_COLUMN_SIZE.M,
            }),
            columnHelper.accessor(
                (conflict) => assigneeName(conflict.responsible),
                {
                    id: "responsible",
                    header: () =>
                        t(
                            "ControlPolicies_SegregationOfDuties_Conflicts_Responsible"
                        ),
                    size: TST_COLUMN_SIZE.S,
                }
            ),
            columnHelper.accessor(
                (conflict) => assigneeName(conflict.reviewer),
                {
                    id: "reviewer",
                    header: () =>
                        t(
                            "ControlPolicies_SegregationOfDuties_Conflicts_Reviewer"
                        ),
                    size: TST_COLUMN_SIZE.S,
                }
            ),
            columnHelper.accessor(
                (conflict) => assigneeName(conflict.secondReviewer),
                {
                    id: "secondReviewer",
                    header: () =>
                        t(
                            "ControlPolicies_SegregationOfDuties_Conflicts_SecondReviewer"
                        ),
                    size: TST_COLUMN_SIZE.S,
                }
            ),
        ];
    }, [t]);

    const table = useReactTable({
        data: conflicts,
        columns,
        enableSorting: false,
        getCoreRowModel: getCoreRowModel(),
    });

    return (
        <TSTTable
            table={table}
            noResultsFallback={t("NoResults")}
            noSourceRowsFallback={t("NoResults")}
        />
    );
}

// A pool clashes through its default claimant, so that is the person to show.
function assigneeName(assignee: ConflictAssignee | null | undefined): string {
    if (!assignee) {
        return "";
    }
    switch (assignee.type) {
        case "User":
            return formatUserName(assignee.user);
        case "Pool":
            return assignee.defaultClaimant
                ? formatUserName(assignee.defaultClaimant)
                : assignee.name;
        case "Group":
            return assignee.name;
        default:
            return assertNever(assignee);
    }
}
